import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { CheckCircle2 } from "lucide-react";

const PLATFORMS = ["Google", "Facebook", "Glassdoor", "Indeed", "Avvo", "Reddit", "RateMDs", "Trustpilot", "Yelp", "BBB", "News article", "Other"];

export default function LeadForm({ compact = false }) {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    source: "",
    platforms: [],
    links: "",
    reason: "",
  });

  const togglePlatform = (p) => {
    setForm((f) => ({
      ...f,
      platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p],
    }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email) {
      toast({ title: "Please complete required fields", description: "First name, last name and email are required." });
      return;
    }
    // Save to localStorage as mock persistence
    const existing = JSON.parse(localStorage.getItem("bmp_leads") || "[]");
    existing.push({ ...form, createdAt: new Date().toISOString() });
    localStorage.setItem("bmp_leads", JSON.stringify(existing));
    setSubmitted(true);
    toast({ title: "Assessment request sent", description: "A specialist will reach out shortly." });
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-10 text-center shadow-sm">
        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        </div>
        <h3 className="text-2xl font-semibold text-[#0B2447] mb-2">Assessment received</h3>
        <p className="text-slate-600 mb-6">Thanks {form.firstName}. A specialist will review your case and reach out within one business day.</p>
        <Button onClick={() => { setSubmitted(false); setForm({ firstName: "", lastName: "", email: "", phone: "", company: "", source: "", platforms: [], links: "", reason: "" }); }} variant="outline">Submit another</Button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm">
      {!compact && (
        <div className="mb-6">
          <h3 className="text-xl md:text-2xl font-semibold text-[#0B2447]">Request your free assessment</h3>
          <p className="text-sm text-slate-500 mt-1">Takes under two minutes. No obligation.</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="firstName">First name *</Label>
          <Input id="firstName" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="lastName">Last name *</Label>
          <Input id="lastName" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="email">Email address *</Label>
          <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="phone">Phone number</Label>
          <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="company">Company name</Label>
          <Input id="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1" />
        </div>
        <div className="md:col-span-2">
          <Label>Where did you hear about us?</Label>
          <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Select an option" /></SelectTrigger>
            <SelectContent>
              {["Email", "Google", "LinkedIn", "Facebook / Instagram", "Referral", "Reddit", "Other"].map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label>Which platform(s) is your content on?</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {PLATFORMS.map((p) => {
              const active = form.platforms.includes(p);
              return (
                <button type="button" key={p} onClick={() => togglePlatform(p)} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${active ? "bg-[#0B2447] text-white border-[#0B2447]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0B2447]"}`}>{p}</button>
              );
            })}
          </div>
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="links">Links to the unwanted content</Label>
          <Textarea id="links" rows={3} placeholder="Optional, but helps us assess your case faster." value={form.links} onChange={(e) => setForm({ ...form, links: e.target.value })} className="mt-1" />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="reason">Why do you feel this content should be removed?</Label>
          <Textarea id="reason" rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1" />
        </div>
        <div className="md:col-span-2 flex items-start gap-2 pt-2">
          <Checkbox id="consent" defaultChecked />
          <label htmlFor="consent" className="text-xs text-slate-500 leading-relaxed">I agree that my details are kept private and never shared with third parties.</label>
        </div>
      </div>
      <Button type="submit" className="mt-6 w-full bg-[#0B2447] hover:bg-[#122e5f] text-white h-12 text-base">Get my free assessment</Button>
      <p className="text-xs text-slate-400 text-center mt-3">Your details are kept private and never shared.</p>
    </form>
  );
}
